import React, { useState, useEffect } from 'react';
import { usePets } from '../contexts/PetContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Plus,
  Edit,
  Trash2,
  Heart,
  Calendar,
  Phone,
  Loader2,
  AlertTriangle,
  CheckCircle,
  X,
  Save,
  User,
  Scale,
  Palette,
  Syringe,
  MapPin,
  BellRing
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const emptyForm = {
  name: '',
  species: '',
  breed: '',
  age: '',
  weight: '',
  color: '',
  medical_history: '',
  allergies: '',
  emergency_contact: '',
  vet_name: '',
  vet_phone: ''
};

const emptyAppointmentForm = {
  pet: '',
  vet_clinic: '',
  vaccine_name: '',
  appointment_at: '',
  clinic_name: '',
  clinic_address: '',
  clinic_phone: '',
  notes: ''
};

const PetManagement = () => {
  const { pets, loading, fetchPets, addPet, updatePet, deletePet, petsHasNext, petsHasPrev, goToPetsNext, goToPetsPrev, petsPage } = usePets();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [appointmentsByPet, setAppointmentsByPet] = useState({});
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [appointmentPet, setAppointmentPet] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState(emptyAppointmentForm);
  const [appointmentError, setAppointmentError] = useState('');
  const [isSavingAppointment, setIsSavingAppointment] = useState(false);
  const [clinicSearch, setClinicSearch] = useState('');
  const [clinicResults, setClinicResults] = useState([]);
  const [isClinicSearching, setIsClinicSearching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const formatSpecies = (species) => {
    if (!species) return '';
    const key = `pets_species_${String(species).toLowerCase()}`;
    const translated = t(key);
    return translated === key ? species : translated;
  };

  const appointmentCopy = {
    en: {
      sectionTitle: 'Vaccine Appointments',
      book: 'Book',
      reminder: 'Reminder scheduled one day earlier',
      empty: 'No vaccine appointment yet.',
      modalTitle: 'Book Vaccine Appointment',
      modalSubtitle: '{petName} will receive a reminder based on the appointment timing.',
      vaccine: 'Vaccine',
      vaccinePlaceholder: 'e.g. Rabies booster',
      appointmentAt: 'Appointment Date & Time',
      clinicSearch: 'Find clinic',
      clinicSearchPlaceholder: 'Search by clinic name, address, district or phone',
      clinicSearchHelp: 'Select a clinic from the AFCD list, or type manually if it is not listed.',
      clinicName: 'Clinic Name',
      clinicNamePlaceholder: 'Clinic or hospital name',
      clinicAddress: 'Clinic Address',
      clinicAddressPlaceholder: 'Address',
      clinicPhone: 'Clinic Phone',
      clinicPhonePlaceholder: 'Phone number',
      notes: 'Notes',
      notesPlaceholder: 'Optional reminders or preparation notes',
      saving: 'Saving...',
      submit: 'Book appointment',
      bookedSuccess: 'Vaccine appointment booked. Reminder will be sent one day before.',
      bookedError: 'Failed to book vaccine appointment.',
      deleteConfirm: 'Delete this vaccine appointment?',
      deletedSuccess: 'Vaccine appointment deleted.',
      deletedError: 'Failed to delete vaccine appointment.'
    },
    'zh-Hant': {
      sectionTitle: '疫苗預約',
      book: '預約',
      reminder: '系統會於前一日發送提醒',
      empty: '暫時未有疫苗預約。',
      modalTitle: '預約疫苗接種',
      modalSubtitle: '{petName} 會按預約時間收到提醒。',
      vaccine: '疫苗名稱',
      vaccinePlaceholder: '例如：狂犬病加強劑',
      appointmentAt: '預約日期及時間',
      clinicSearch: '搜尋診所',
      clinicSearchPlaceholder: '輸入診所名稱、地址、地區或電話',
      clinicSearchHelp: '可從 AFCD 診所名單選擇；如果找不到，也可以手動輸入。',
      clinicName: '診所名稱',
      clinicNamePlaceholder: '診所或動物醫院名稱',
      clinicAddress: '診所地址',
      clinicAddressPlaceholder: '地址',
      clinicPhone: '診所電話',
      clinicPhonePlaceholder: '電話號碼',
      notes: '備註',
      notesPlaceholder: '可填寫準備事項或提醒',
      saving: '儲存中...',
      submit: '建立預約',
      bookedSuccess: '疫苗預約已建立，系統會於前一日提醒。',
      bookedError: '未能建立疫苗預約。',
      deleteConfirm: '確定刪除這個疫苗預約？',
      deletedSuccess: '疫苗預約已刪除。',
      deletedError: '未能刪除疫苗預約。'
    },
    'zh-CN': {
      sectionTitle: '疫苗预约',
      book: '预约',
      reminder: '系统会在前一日发送提醒',
      empty: '暂无疫苗预约。',
      modalTitle: '预约疫苗接种',
      modalSubtitle: '{petName} 会按预约时间收到提醒。',
      vaccine: '疫苗名称',
      vaccinePlaceholder: '例如：狂犬病加强针',
      appointmentAt: '预约日期和时间',
      clinicSearch: '搜索诊所',
      clinicSearchPlaceholder: '输入诊所名称、地址、地区或电话',
      clinicSearchHelp: '可从 AFCD 诊所名单选择；如果找不到，也可以手动输入。',
      clinicName: '诊所名称',
      clinicNamePlaceholder: '诊所或动物医院名称',
      clinicAddress: '诊所地址',
      clinicAddressPlaceholder: '地址',
      clinicPhone: '诊所电话',
      clinicPhonePlaceholder: '电话号码',
      notes: '备注',
      notesPlaceholder: '可填写准备事项或提醒',
      saving: '保存中...',
      submit: '创建预约',
      bookedSuccess: '疫苗预约已创建，系统会在前一日提醒。',
      bookedError: '未能创建疫苗预约。',
      deleteConfirm: '确定删除这个疫苗预约？',
      deletedSuccess: '疫苗预约已删除。',
      deletedError: '未能删除疫苗预约。'
    }
  };

  const appointmentText = (key, params = {}) => {
    const template = appointmentCopy[language]?.[key] || appointmentCopy.en[key] || key;
    return template.replace(/\{(\w+)\}/g, (_, token) => params[token] ?? '');
  };

  const appointmentLocale = language === 'zh-CN' ? 'zh-CN' : language === 'zh-Hant' ? 'zh-HK' : 'en-HK';

  useEffect(() => {
    if (user) {
      fetchPets();
      fetchAppointments();
    }
  }, [user, fetchPets]);

  useEffect(() => {
    if (!isAppointmentModalOpen) return undefined;
    const query = clinicSearch.trim();
    if (query.length < 2) {
      setClinicResults([]);
      setIsClinicSearching(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setIsClinicSearching(true);
      try {
        const response = await axios.get('/vet-clinics/', { params: { q: query } });
        const results = response.data?.results || response.data || [];
        if (!cancelled) setClinicResults(results.slice(0, 8));
      } catch (clinicError) {
        console.error('Failed to search vet clinics:', clinicError);
        if (!cancelled) setClinicResults([]);
      } finally {
        if (!cancelled) setIsClinicSearching(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [clinicSearch, isAppointmentModalOpen]);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get('/vaccine-appointments/');
      const grouped = (response.data?.results || response.data || []).reduce((acc, item) => {
        const key = item.pet;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});
      setAppointmentsByPet(grouped);
    } catch (loadError) {
      console.error('Failed to load vaccine appointments:', loadError);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const formatApiError = (errValue) => {
    if (!errValue) return t('pets_save_failed');
    if (typeof errValue === 'string') return errValue;
    if (Array.isArray(errValue)) return errValue.join(', ');
    if (typeof errValue === 'object') {
      return Object.entries(errValue)
        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
        .join(' | ');
    }
    return t('pets_save_failed');
  };

  const buildPetPayload = () => ({
    name: (formData.name || '').trim(),
    species: (formData.species || '').toLowerCase(),
    breed: (formData.breed || '').trim(),
    age: formData.age === '' ? null : Number(formData.age),
    weight: formData.weight === '' ? null : Number(formData.weight),
    color: (formData.color || '').trim(),
    medical_history: (formData.medical_history || '').trim(),
    allergies: (formData.allergies || '').trim(),
    emergency_contact: (formData.emergency_contact || '').trim(),
    vet_name: (formData.vet_name || '').trim(),
    vet_phone: (formData.vet_phone || '').trim()
  });

  const resetModalState = () => {
    setFormData(emptyForm);
    setEditingPet(null);
    setIsModalOpen(false);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const payload = buildPetPayload();
      if (editingPet) {
        const res = await updatePet(editingPet.id, payload);
        if (!res?.success) throw new Error(formatApiError(res?.error) || t('pets_update_failed'));
        setSuccess(t('pets_update_success'));
      } else {
        const res = await addPet(payload);
        if (!res?.success) throw new Error(formatApiError(res?.error) || t('pets_add_failed'));
        setSuccess(t('pets_add_success'));
      }
      resetModalState();
    } catch (err) {
      setError(err?.message || t('pets_save_failed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (pet) => {
    setEditingPet(pet);
    setFormData({
      name: pet.name || '',
      species: pet.species || '',
      breed: pet.breed || '',
      age: pet.age || '',
      weight: pet.weight || '',
      color: pet.color || '',
      medical_history: pet.medical_history || '',
      allergies: pet.allergies || '',
      emergency_contact: pet.emergency_contact || '',
      vet_name: pet.vet_name || '',
      vet_phone: pet.vet_phone || ''
    });
    setError('');
    setSuccess('');
    setIsModalOpen(true);
  };

  const handleDelete = async (petId) => {
    if (!window.confirm(t('pets_delete_confirm'))) return;
    try {
      await deletePet(petId);
      setSuccess(t('pets_delete_success'));
    } catch (err) {
      setError(t('pets_delete_failed'));
    }
  };

  const openAddModal = () => {
    setFormData(emptyForm);
    setEditingPet(null);
    setError('');
    setSuccess('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPet(null);
    setError('');
  };

  const openAppointmentModal = (pet) => {
    setAppointmentPet(pet);
    setAppointmentForm({ ...emptyAppointmentForm, pet: pet.id });
    setClinicSearch('');
    setClinicResults([]);
    setAppointmentError('');
    setIsAppointmentModalOpen(true);
  };

  const closeAppointmentModal = () => {
    setIsAppointmentModalOpen(false);
    setAppointmentPet(null);
    setAppointmentForm(emptyAppointmentForm);
    setClinicSearch('');
    setClinicResults([]);
    setAppointmentError('');
  };

  const handleAppointmentInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'clinic_name') {
      setClinicSearch(value);
      setAppointmentForm((prev) => ({ ...prev, vet_clinic: '', clinic_name: value }));
      return;
    }
    setAppointmentForm((prev) => ({ ...prev, [name]: value }));
  };

  const selectVetClinic = (clinic) => {
    const displayName = clinic.display_name || clinic.name_zh || clinic.name || '';
    const displayAddress = clinic.display_address || clinic.address_zh || clinic.address || '';
    setAppointmentForm((prev) => ({
      ...prev,
      vet_clinic: clinic.id,
      clinic_name: displayName,
      clinic_address: displayAddress,
      clinic_phone: clinic.phone || ''
    }));
    setClinicSearch(displayName);
    setClinicResults([]);
  };

  const submitAppointment = async (e) => {
    e.preventDefault();
    if (!appointmentPet) return;
    setIsSavingAppointment(true);
    setAppointmentError('');

    try {
      await axios.post('/vaccine-appointments/', appointmentForm);
      await fetchAppointments();
      setSuccess(appointmentText('bookedSuccess'));
      closeAppointmentModal();
    } catch (saveError) {
      console.error('Failed to save appointment:', saveError);
      setAppointmentError(appointmentText('bookedError'));
    } finally {
      setIsSavingAppointment(false);
    }
  };

  const deleteAppointment = async (appointmentId) => {
    if (!window.confirm(appointmentText('deleteConfirm'))) return;
    try {
      await axios.delete(`/vaccine-appointments/${appointmentId}/`);
      await fetchAppointments();
      setSuccess(appointmentText('deletedSuccess'));
    } catch (deleteError) {
      console.error('Failed to delete appointment:', deleteError);
      setError(appointmentText('deletedError'));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-12 w-12 animate-spin text-blue-600" />
          <p className="text-gray-600">{t('loading_pets')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">{t('pets_title')}</h2>
              <p className="mt-1 text-sm text-gray-500">{t('pets_subtitle')}</p>
            </div>
            <div className="mt-4 flex md:ml-4 md:mt-0">
              <button onClick={openAddModal} className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                <Plus className="mr-2 h-4 w-4" />
                {t('add_pet')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl py-6 sm:px-6 lg:px-8">
        {success && (
          <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-4">
            <div className="flex">
              <CheckCircle className="h-5 w-5 flex-shrink-0 text-green-400" />
              <div className="ml-3">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {pets && pets.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {pets.map((pet) => (
              <div key={pet.id} className="overflow-hidden rounded-lg bg-white shadow">
                <div className="px-4 py-5 sm:p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
                        <Heart className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-gray-900">{pet.name}</h3>
                        <p className="text-sm text-gray-500">{formatSpecies(pet.species)}{pet.breed ? ` - ${pet.breed}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button onClick={() => handleEdit(pet)} className="p-2 text-gray-400 transition-colors hover:text-blue-600">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(pet.id)} className="p-2 text-gray-400 transition-colors hover:text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="mr-2 h-4 w-4" />
                      <span>{t('pets_age')}: {pet.age}</span>
                    </div>

                    {pet.weight && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Scale className="mr-2 h-4 w-4" />
                        <span>{t('pets_weight_lbs')}: {pet.weight}</span>
                      </div>
                    )}

                    {pet.color && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Palette className="mr-2 h-4 w-4" />
                        <span>{t('pets_color')}: {pet.color}</span>
                      </div>
                    )}

                    {pet.vet_name && (
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="mr-2 h-4 w-4" />
                        <span>{t('pets_vet_name')}: {pet.vet_name}</span>
                      </div>
                    )}

                    {pet.emergency_contact && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="mr-2 h-4 w-4" />
                        <span>{t('pets_emergency_contact')}: {pet.emergency_contact}</span>
                      </div>
                    )}
                  </div>

                  {pet.medical_history && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <h4 className="mb-2 text-sm font-medium text-gray-900">{t('pets_medical_history')}</h4>
                      <p className="line-clamp-2 text-sm text-gray-600">{pet.medical_history}</p>
                    </div>
                  )}

                  {pet.allergies && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <h4 className="mb-2 text-sm font-medium text-gray-900">{t('pets_allergies')}</h4>
                      <p className="text-sm text-gray-600">{pet.allergies}</p>
                    </div>
                  )}

                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">{appointmentText('sectionTitle')}</h4>
                      <button
                        onClick={() => openAppointmentModal(pet)}
                        className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-xs font-medium text-sky-700 transition-colors hover:bg-sky-200"
                      >
                        <Syringe className="mr-1 h-3.5 w-3.5" />
                        {appointmentText('book')}
                      </button>
                    </div>

                    {(appointmentsByPet[pet.id] || []).length > 0 ? (
                      <div className="space-y-2">
                        {appointmentsByPet[pet.id].slice(0, 2).map((appointment) => (
                          <div key={appointment.id} className="rounded-2xl border border-sky-100 bg-sky-50/70 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">{appointment.vaccine_name}</p>
                                <p className="mt-1 flex items-center text-xs text-slate-600">
                                  <Calendar className="mr-1 h-3.5 w-3.5" />
                                  {new Date(appointment.appointment_at).toLocaleString(appointmentLocale)}
                                </p>
                                {appointment.clinic_name && (
                                  <p className="mt-1 flex items-center text-xs text-slate-600">
                                    <MapPin className="mr-1 h-3.5 w-3.5" />
                                    {appointment.clinic_name}
                                  </p>
                                )}
                                <p className="mt-1 flex items-center text-xs text-amber-700">
                                  <BellRing className="mr-1 h-3.5 w-3.5" />
                                  {appointmentText('reminder')}
                                </p>
                              </div>
                              <button onClick={() => deleteAppointment(appointment.id)} className="p-1 text-gray-400 transition-colors hover:text-red-600">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">{appointmentText('empty')}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-12 text-center">
            <Heart className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">{t('dash_no_pets')}</h3>
            <p className="mb-6 text-gray-600">{t('dash_start_first_assessment')}</p>
            <button onClick={openAddModal} className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              <Plus className="mr-2 h-4 w-4" />
              {t('add_pet')}
            </button>
          </div>
        )}

        <div className="mt-6 flex items-center justify-center space-x-3">
          <button onClick={goToPetsPrev} disabled={!petsHasPrev} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 disabled:opacity-50">
            {t('back')}
          </button>
          <span className="text-sm text-gray-600">{t('page')} {petsPage}</span>
          <button onClick={goToPetsNext} disabled={!petsHasNext} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-gray-700 disabled:opacity-50">
            {t('next')}
          </button>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-gray-600/50">
          <div className="relative top-20 mx-auto w-11/12 rounded-md border bg-white p-5 shadow-lg md:w-3/4 lg:w-1/2">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{editingPet ? t('edit_pet') : t('add_new_pet')}</h3>
              <button onClick={closeModal} className="text-gray-400 transition-colors hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">{t('pets_pet_name')}</label>
                  <input type="text" name="name" id="name" required value={formData.name} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={t('pets_placeholder_pet_name')} />
                </div>

                <div>
                  <label htmlFor="species" className="block text-sm font-medium text-gray-700">{t('pets_species')}</label>
                  <select name="species" id="species" required value={formData.species} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm">
                    <option value="">{t('pets_select_species')}</option>
                    <option value="dog">{t('pets_species_dog')}</option>
                    <option value="cat">{t('pets_species_cat')}</option>
                    <option value="bird">{t('pets_species_bird')}</option>
                    <option value="rabbit">{t('pets_species_rabbit')}</option>
                    <option value="hamster">{t('pets_species_hamster')}</option>
                    <option value="other">{t('pets_species_other')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="breed" className="block text-sm font-medium text-gray-700">{t('pets_breed')}</label>
                  <input type="text" name="breed" id="breed" value={formData.breed} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={t('pets_placeholder_breed')} />
                </div>

                <div>
                  <label htmlFor="age" className="block text-sm font-medium text-gray-700">{t('pets_age_years')}</label>
                  <input type="number" name="age" id="age" min="0" max="30" value={formData.age} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={t('pets_placeholder_age')} />
                </div>

                <div>
                  <label htmlFor="weight" className="block text-sm font-medium text-gray-700">{t('pets_weight_lbs')}</label>
                  <input type="number" name="weight" id="weight" min="0" value={formData.weight} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={t('pets_placeholder_weight')} />
                </div>

                <div>
                  <label htmlFor="color" className="block text-sm font-medium text-gray-700">{t('pets_color')}</label>
                  <input type="text" name="color" id="color" value={formData.color} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={t('pets_placeholder_color')} />
                </div>
              </div>

              <div>
                <label htmlFor="medical_history" className="block text-sm font-medium text-gray-700">{t('pets_medical_history')}</label>
                <textarea name="medical_history" id="medical_history" rows="3" value={formData.medical_history} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={t('pets_placeholder_medical_history')} />
              </div>

              <div>
                <label htmlFor="allergies" className="block text-sm font-medium text-gray-700">{t('pets_allergies')}</label>
                <textarea name="allergies" id="allergies" rows="2" value={formData.allergies} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={t('pets_placeholder_allergies')} />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="vet_name" className="block text-sm font-medium text-gray-700">{t('pets_vet_name')}</label>
                  <input type="text" name="vet_name" id="vet_name" value={formData.vet_name} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={t('pets_placeholder_vet_name')} />
                </div>

                <div>
                  <label htmlFor="vet_phone" className="block text-sm font-medium text-gray-700">{t('pets_vet_phone')}</label>
                  <input type="tel" name="vet_phone" id="vet_phone" value={formData.vet_phone} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={t('pets_placeholder_vet_phone')} />
                </div>
              </div>

              <div>
                <label htmlFor="emergency_contact" className="block text-sm font-medium text-gray-700">{t('pets_emergency_contact')}</label>
                <input type="text" name="emergency_contact" id="emergency_contact" value={formData.emergency_contact} onChange={handleInputChange} className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={t('pets_placeholder_emergency_contact')} />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={closeModal} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                  {t('cancel')}
                </button>
                <button type="submit" disabled={isSubmitting} className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('pets_saving')}
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {editingPet ? t('pets_update_pet') : t('add_pet')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isAppointmentModalOpen && appointmentPet && (
        <div className="fixed inset-0 z-50 h-full w-full overflow-y-auto bg-slate-900/45">
          <div className="relative top-20 mx-auto w-11/12 rounded-3xl border border-white/70 bg-white p-6 shadow-2xl md:w-2/3 lg:w-[32rem]">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{appointmentText('modalTitle')}</h3>
                <p className="text-sm text-slate-500">{appointmentText('modalSubtitle', { petName: appointmentPet.name })}</p>
              </div>
              <button onClick={closeAppointmentModal} className="text-gray-400 transition-colors hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4 rounded-2xl border border-sky-100 bg-sky-50/85 p-4">
              <p className="text-sm font-semibold text-sky-900">{t('pets_vaccine_notice_title')}</p>
              <ul className="mt-2 space-y-1 text-sm text-sky-800">
                <li>{t('pets_vaccine_notice_over_24h')}</li>
                <li>{t('pets_vaccine_notice_within_24h')}</li>
              </ul>
            </div>

            {appointmentError && (
              <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {appointmentError}
              </div>
            )}

            <form onSubmit={submitAppointment} className="space-y-4">
              <div>
                <label htmlFor="vaccine_name" className="block text-sm font-medium text-gray-700">{appointmentText('vaccine')}</label>
                <input type="text" name="vaccine_name" id="vaccine_name" required value={appointmentForm.vaccine_name} onChange={handleAppointmentInputChange} className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={appointmentText('vaccinePlaceholder')} />
              </div>

              <div>
                <label htmlFor="appointment_at" className="block text-sm font-medium text-gray-700">{appointmentText('appointmentAt')}</label>
                <input type="datetime-local" name="appointment_at" id="appointment_at" required value={appointmentForm.appointment_at} onChange={handleAppointmentInputChange} className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" />
              </div>

              <div>
                <label htmlFor="clinic_name" className="block text-sm font-medium text-gray-700">{appointmentText('clinicSearch')}</label>
                <div className="relative">
                  <input type="text" name="clinic_name" id="clinic_name" value={appointmentForm.clinic_name} onChange={handleAppointmentInputChange} autoComplete="off" className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 pr-10 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={appointmentText('clinicSearchPlaceholder')} />
                  {isClinicSearching && (
                    <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-sky-500" />
                  )}
                  {clinicResults.length > 0 && (
                    <div className="absolute z-20 mt-2 max-h-64 w-full overflow-y-auto rounded-2xl border border-sky-100 bg-white shadow-2xl">
                      {clinicResults.map((clinic) => (
                        <button
                          type="button"
                          key={clinic.id}
                          onClick={() => selectVetClinic(clinic)}
                          className="block w-full border-b border-slate-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-sky-50"
                        >
                          <span className="block text-sm font-semibold text-slate-900">{clinic.display_name || clinic.name}</span>
                          <span className="mt-1 block text-xs text-slate-600">{clinic.display_address || clinic.address}</span>
                          <span className="mt-1 block text-xs text-sky-700">{clinic.district_zh || clinic.district}{clinic.phone ? ` · ${clinic.phone}` : ''}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">{appointmentText('clinicSearchHelp')}</p>
              </div>

              <div>
                <label htmlFor="clinic_address" className="block text-sm font-medium text-gray-700">{appointmentText('clinicAddress')}</label>
                <input type="text" name="clinic_address" id="clinic_address" value={appointmentForm.clinic_address} onChange={handleAppointmentInputChange} className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={appointmentText('clinicAddressPlaceholder')} />
              </div>

              <div>
                <label htmlFor="clinic_phone" className="block text-sm font-medium text-gray-700">{appointmentText('clinicPhone')}</label>
                <input type="tel" name="clinic_phone" id="clinic_phone" value={appointmentForm.clinic_phone} onChange={handleAppointmentInputChange} className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={appointmentText('clinicPhonePlaceholder')} />
              </div>

              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700">{appointmentText('notes')}</label>
                <textarea name="notes" id="notes" rows="3" value={appointmentForm.notes} onChange={handleAppointmentInputChange} className="mt-1 block w-full rounded-xl border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm" placeholder={appointmentText('notesPlaceholder')} />
              </div>

              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={closeAppointmentModal} className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50">
                  {t('cancel')}
                </button>
                <button type="submit" disabled={isSavingAppointment} className="inline-flex items-center rounded-xl border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                  {isSavingAppointment ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Syringe className="mr-2 h-4 w-4" />}
                  {isSavingAppointment ? appointmentText('saving') : appointmentText('submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PetManagement;
